import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Popover from "@mui/material/Popover";
import Select from "@mui/material/Select";
import Slider from "@mui/material/Slider";
import Switch from "@mui/material/Switch";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import PauseOutlinedIcon from "@mui/icons-material/PauseOutlined";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import TextFieldsOutlinedIcon from "@mui/icons-material/TextFieldsOutlined";

const MIN_FONT_SIZE = 14;
const MAX_FONT_SIZE = 42;
const DEFAULT_FONT_SIZE = 22;

const MIN_SCROLL_SPEED = 8;
const MAX_SCROLL_SPEED = 140;
const DEFAULT_SCROLL_SPEED = 32;

const DEFAULT_WIDTH = 560;
const DEFAULT_HEIGHT = 340;
const MIN_WIDTH = 360;
const MIN_HEIGHT = 240;
const VIEWPORT_MARGIN = 12;
const RESIZE_HANDLE_SIZE = 10;

const getDefaultPosition = () => {
    if (typeof window === "undefined") {
        return { x: VIEWPORT_MARGIN, y: VIEWPORT_MARGIN };
    }
    const x = Math.max(VIEWPORT_MARGIN, Math.round((window.innerWidth - DEFAULT_WIDTH) / 2));
    const y = Math.max(VIEWPORT_MARGIN, Math.round(window.innerHeight - DEFAULT_HEIGHT - 140));
    return { x, y };
};

const TeleprompterWindow = ({ open, onClose, recordingInProgress }) => {
    const [text, setText] = useState("");
    const [fontSize, setFontSize] = useState(DEFAULT_FONT_SIZE);
    const [scrollSpeed, setScrollSpeed] = useState(DEFAULT_SCROLL_SPEED);
    const [scrollMode, setScrollMode] = useState("variable");
    const [isPlaying, setIsPlaying] = useState(false);
    const [autoScrollOnRecord, setAutoScrollOnRecord] = useState(true);
    const [focusMode, setFocusMode] = useState(false);
    const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
    const [position, setPosition] = useState(getDefaultPosition);
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [size, setSize] = useState({ width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT });

    const containerRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const textareaRef = useRef(null);
    const fileInputRef = useRef(null);
    const animationRef = useRef(null);
    const lastTimestampRef = useRef(null);
    const lockedSpeedRef = useRef(scrollSpeed);
    const previousRecordingRef = useRef(recordingInProgress);
    const dragStateRef = useRef(null);
    const resizeStateRef = useRef(null);

    const settingsOpen = Boolean(settingsAnchorEl);
    const speedLocked = scrollMode === "constant" && isPlaying;

    const clampToViewport = useCallback((x, y) => {
        if (typeof window === "undefined") {
            return { x, y };
        }
        const width = containerRef.current?.offsetWidth ?? DEFAULT_WIDTH;
        const height = containerRef.current?.offsetHeight ?? DEFAULT_HEIGHT;
        const maxX = Math.max(VIEWPORT_MARGIN, window.innerWidth - width - VIEWPORT_MARGIN);
        const maxY = Math.max(VIEWPORT_MARGIN, window.innerHeight - height - VIEWPORT_MARGIN);
        return {
            x: Math.min(Math.max(VIEWPORT_MARGIN, x), maxX),
            y: Math.min(Math.max(VIEWPORT_MARGIN, y), maxY),
        };
    }, []);

    const clampRect = useCallback((x, y, width, height) => {
        if (typeof window === "undefined") {
            return { x, y, width, height };
        }
        const maxWidth = Math.max(MIN_WIDTH, window.innerWidth - VIEWPORT_MARGIN * 2);
        const maxHeight = Math.max(MIN_HEIGHT, window.innerHeight - VIEWPORT_MARGIN * 2);
        const clampedWidth = Math.min(Math.max(MIN_WIDTH, width), maxWidth);
        const clampedHeight = Math.min(Math.max(MIN_HEIGHT, height), maxHeight);
        const maxX = Math.max(VIEWPORT_MARGIN, window.innerWidth - clampedWidth - VIEWPORT_MARGIN);
        const maxY = Math.max(VIEWPORT_MARGIN, window.innerHeight - clampedHeight - VIEWPORT_MARGIN);
        return {
            x: Math.min(Math.max(VIEWPORT_MARGIN, x), maxX),
            y: Math.min(Math.max(VIEWPORT_MARGIN, y), maxY),
            width: clampedWidth,
            height: clampedHeight,
        };
    }, []);

    const handleDragMove = useCallback((event) => {
        if (!dragStateRef.current) return;
        const { startX, startY, originX, originY } = dragStateRef.current;
        const nextX = originX + (event.clientX - startX);
        const nextY = originY + (event.clientY - startY);
        const clamped = clampToViewport(nextX, nextY);
        setPosition(clamped);
    }, [clampToViewport]);

    const handleDragEnd = useCallback(() => {
        dragStateRef.current = null;
        setIsDragging(false);
        window.removeEventListener("pointermove", handleDragMove);
        window.removeEventListener("pointerup", handleDragEnd);
    }, [handleDragMove]);

    const handleDragStart = useCallback((event) => {
        if (event.button !== 0) return;
        const container = containerRef.current;
        if (!container) return;
        event.preventDefault();
        event.stopPropagation();
        const rect = container.getBoundingClientRect();
        dragStateRef.current = {
            startX: event.clientX,
            startY: event.clientY,
            originX: rect.left,
            originY: rect.top,
        };
        setIsDragging(true);
        window.addEventListener("pointermove", handleDragMove);
        window.addEventListener("pointerup", handleDragEnd);
    }, [handleDragMove, handleDragEnd]);

    const handleResizeMove = useCallback((event) => {
        if (!resizeStateRef.current) return;
        const {
            startX,
            startY,
            startWidth,
            startHeight,
            startLeft,
            startTop,
            dir,
        } = resizeStateRef.current;
        const dx = event.clientX - startX;
        const dy = event.clientY - startY;
        let nextWidth = startWidth;
        let nextHeight = startHeight;
        let nextLeft = startLeft;
        let nextTop = startTop;

        if (dir.includes("e")) {
            nextWidth = startWidth + dx;
        }
        if (dir.includes("s")) {
            nextHeight = startHeight + dy;
        }
        if (dir.includes("w")) {
            nextWidth = startWidth - dx;
            nextLeft = startLeft + dx;
        }
        if (dir.includes("n")) {
            nextHeight = startHeight - dy;
            nextTop = startTop + dy;
        }

        const clamped = clampRect(nextLeft, nextTop, nextWidth, nextHeight);
        let adjustedX = clamped.x;
        let adjustedY = clamped.y;
        if (dir.includes("w")) {
            adjustedX = startLeft + (startWidth - clamped.width);
        }
        if (dir.includes("n")) {
            adjustedY = startTop + (startHeight - clamped.height);
        }
        const finalRect = clampRect(adjustedX, adjustedY, clamped.width, clamped.height);

        setSize({ width: finalRect.width, height: finalRect.height });
        setPosition({ x: finalRect.x, y: finalRect.y });
    }, [clampRect]);

    const handleResizeEnd = useCallback(() => {
        resizeStateRef.current = null;
        setIsResizing(false);
        document.body.style.userSelect = "";
        window.removeEventListener("pointermove", handleResizeMove);
        window.removeEventListener("pointerup", handleResizeEnd);
    }, [handleResizeMove]);

    const handleResizeStart = useCallback((event, dir) => {
        if (event.button !== 0) return;
        const container = containerRef.current;
        if (!container) return;
        event.preventDefault();
        event.stopPropagation();
        const rect = container.getBoundingClientRect();
        resizeStateRef.current = {
            startX: event.clientX,
            startY: event.clientY,
            startWidth: rect.width,
            startHeight: rect.height,
            startLeft: rect.left,
            startTop: rect.top,
            dir,
        };
        setIsResizing(true);
        document.body.style.userSelect = "none";
        window.addEventListener("pointermove", handleResizeMove);
        window.addEventListener("pointerup", handleResizeEnd);
    }, [handleResizeMove, handleResizeEnd]);

    const renderResizeHandle = (dir, style, cursor) => (
        <Box
            onPointerDown={(event) => handleResizeStart(event, dir)}
            sx={{
                position: "absolute",
                ...style,
                cursor,
                zIndex: 3,
                backgroundColor: "rgba(17,24,39,0.03)",
                "&:hover": {
                    backgroundColor: "rgba(17,24,39,0.08)",
                },
            }}
        />
    );

    useLayoutEffect(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = "auto";
        el.style.height = `${el.scrollHeight}px`;
    }, [text, fontSize, open]);

    useEffect(() => {
        if (!open) {
            setIsPlaying(false);
            setIsDragging(false);
            setIsResizing(false);
            window.removeEventListener("pointermove", handleDragMove);
            window.removeEventListener("pointerup", handleDragEnd);
            window.removeEventListener("pointermove", handleResizeMove);
            window.removeEventListener("pointerup", handleResizeEnd);
            return;
        }
        setPosition((prev) => {
            const clamped = clampToViewport(prev.x, prev.y);
            if (clamped.x === prev.x && clamped.y === prev.y) return prev;
            return clamped;
        });
        return undefined;
    }, [open, clampToViewport, handleDragMove, handleDragEnd, handleResizeMove, handleResizeEnd]);

    useEffect(() => {
        if (!open || !isPlaying) {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
            lastTimestampRef.current = null;
            return undefined;
        }

        const step = (timestamp) => {
            const container = scrollContainerRef.current;
            if (!container) {
                animationRef.current = requestAnimationFrame(step);
                return;
            }

            if (lastTimestampRef.current === null) {
                lastTimestampRef.current = timestamp;
            }

            const delta = timestamp - lastTimestampRef.current;
            lastTimestampRef.current = timestamp;

            const maxScroll = container.scrollHeight - container.clientHeight;
            if (maxScroll <= 0) {
                setIsPlaying(false);
                return;
            }

            const speed = scrollMode === "constant" ? lockedSpeedRef.current : scrollSpeed;
            const next = Math.min(maxScroll, container.scrollTop + (delta / 1000) * speed);
            container.scrollTop = next;

            if (next >= maxScroll - 1) {
                setIsPlaying(false);
                return;
            }

            animationRef.current = requestAnimationFrame(step);
        };

        animationRef.current = requestAnimationFrame(step);
        return () => {
            if (animationRef.current) {
                cancelAnimationFrame(animationRef.current);
            }
        };
    }, [open, isPlaying, scrollMode, scrollSpeed]);

    useEffect(() => {
        if (scrollMode === "constant" && isPlaying) {
            lockedSpeedRef.current = scrollSpeed;
        }
    }, [scrollMode, isPlaying, scrollSpeed]);

    useEffect(() => {
        if (!open) {
            previousRecordingRef.current = recordingInProgress;
            return;
        }

        const wasRecording = previousRecordingRef.current;
        if (autoScrollOnRecord && recordingInProgress && !wasRecording) {
            if (scrollMode === "constant") {
                lockedSpeedRef.current = scrollSpeed;
            }
            lastTimestampRef.current = null;
            setIsPlaying(true);
        }

        if (!recordingInProgress && wasRecording) {
            setIsPlaying(false);
        }

        previousRecordingRef.current = recordingInProgress;
    }, [recordingInProgress, autoScrollOnRecord, open, scrollMode, scrollSpeed]);

    useEffect(() => {
        if (!open) return;
        const handleResize = () => {
            setPosition((prev) => clampToViewport(prev.x, prev.y));
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, [open, clampToViewport]);

    useEffect(() => {
        if (!open || !containerRef.current || typeof ResizeObserver === "undefined") return;
        const observer = new ResizeObserver(() => {
            const width = containerRef.current?.offsetWidth ?? DEFAULT_WIDTH;
            const height = containerRef.current?.offsetHeight ?? DEFAULT_HEIGHT;
            setSize((prev) => {
                if (Math.abs(prev.width - width) < 1 && Math.abs(prev.height - height) < 1) {
                    return prev;
                }
                return { width, height };
            });
            setPosition((prev) => clampToViewport(prev.x, prev.y));
        });
        observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, [open, clampToViewport]);

    useEffect(() => {
        return () => {
            window.removeEventListener("pointermove", handleDragMove);
            window.removeEventListener("pointerup", handleDragEnd);
            window.removeEventListener("pointermove", handleResizeMove);
            window.removeEventListener("pointerup", handleResizeEnd);
        };
    }, [handleDragMove, handleDragEnd, handleResizeMove, handleResizeEnd]);

    const handleTogglePlay = () => {
        if (!isPlaying && scrollMode === "constant") {
            lockedSpeedRef.current = scrollSpeed;
        }
        lastTimestampRef.current = null;
        setIsPlaying((prev) => !prev);
    };

    const handleRestart = () => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
        }
        lastTimestampRef.current = null;
        setIsPlaying(false);
    };

    const handleImportClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            setText(String(reader.result || ""));
        };
        reader.readAsText(file);
        event.target.value = "";
    };

    return (
        <Box
            ref={containerRef}
            sx={{
                position: "fixed",
                left: position.x,
                top: position.y,
                width: size.width,
                maxWidth: "96vw",
                height: size.height,
                maxHeight: "85vh",
                backgroundColor: "#ffffff",
                border: "1px solid #E5E7EB",
                borderRadius: "12px",
                boxShadow: "0 20px 60px rgba(15, 23, 42, 0.08)",
                display: open ? "flex" : "none",
                flexDirection: "column",
                resize: "none",
                overflow: "hidden",
                minWidth: MIN_WIDTH,
                minHeight: MIN_HEIGHT,
                boxSizing: "border-box",
                zIndex: 1400,
            }}
        >
            {renderResizeHandle(
                "n",
                { top: 0, left: RESIZE_HANDLE_SIZE, right: RESIZE_HANDLE_SIZE, height: RESIZE_HANDLE_SIZE },
                "ns-resize"
            )}
            {renderResizeHandle(
                "s",
                { bottom: 0, left: RESIZE_HANDLE_SIZE, right: RESIZE_HANDLE_SIZE, height: RESIZE_HANDLE_SIZE },
                "ns-resize"
            )}
            {renderResizeHandle(
                "e",
                { right: 0, top: RESIZE_HANDLE_SIZE, bottom: RESIZE_HANDLE_SIZE, width: RESIZE_HANDLE_SIZE },
                "ew-resize"
            )}
            {renderResizeHandle(
                "w",
                { left: 0, top: RESIZE_HANDLE_SIZE, bottom: RESIZE_HANDLE_SIZE, width: RESIZE_HANDLE_SIZE },
                "ew-resize"
            )}
            {renderResizeHandle(
                "ne",
                { top: 0, right: 0, width: RESIZE_HANDLE_SIZE, height: RESIZE_HANDLE_SIZE },
                "nesw-resize"
            )}
            {renderResizeHandle(
                "nw",
                { top: 0, left: 0, width: RESIZE_HANDLE_SIZE, height: RESIZE_HANDLE_SIZE },
                "nwse-resize"
            )}
            {renderResizeHandle(
                "se",
                { bottom: 0, right: 0, width: RESIZE_HANDLE_SIZE, height: RESIZE_HANDLE_SIZE },
                "nwse-resize"
            )}
            {renderResizeHandle(
                "sw",
                { bottom: 0, left: 0, width: RESIZE_HANDLE_SIZE, height: RESIZE_HANDLE_SIZE },
                "nesw-resize"
            )}
            <Box
                onPointerDown={handleDragStart}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2,
                    py: 1,
                    backgroundColor: "#FAFAFA",
                    borderBottom: "1px solid #E5E7EB",
                    cursor: isDragging ? "grabbing" : (isResizing ? "default" : "grab"),
                    userSelect: "none",
                }}
            >
                <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#1F2937" }}>
                    Teleprompter
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Tooltip title="Import text file">
                        <IconButton
                            size="small"
                            onPointerDown={(event) => event.stopPropagation()}
                            onClick={handleImportClick}
                        >
                            <FileUploadOutlinedIcon sx={{ fontSize: 18, color: "#6B7280" }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Close">
                        <IconButton
                            size="small"
                            onPointerDown={(event) => event.stopPropagation()}
                            onClick={onClose}
                        >
                            <CloseIcon sx={{ fontSize: 18, color: "#6B7280" }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            <Box sx={{ position: "relative", flex: 1, minHeight: 0 }}>
                <Box
                    ref={scrollContainerRef}
                    sx={{
                        position: "absolute",
                        inset: 0,
                        overflowY: "auto",
                        px: 2.5,
                        py: 2,
                    }}
                >
                    <textarea
                        ref={textareaRef}
                        value={text}
                        onChange={(event) => setText(event.target.value)}
                        placeholder="Paste or type your script here…"
                        style={{
                            width: "100%",
                            border: "none",
                            outline: "none",
                            resize: "none",
                            background: "transparent",
                            fontFamily: "Inter, sans-serif",
                            fontSize: `${fontSize}px`,
                            lineHeight: 1.6,
                            color: "#111827",
                        }}
                    />
                    {!text && (
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 1,
                                color: "#9CA3AF",
                                pointerEvents: "none",
                            }}
                        >
                            <Typography sx={{ fontSize: "14px" }}>
                                Paste, type, or import a script
                            </Typography>
                            <Box
                                sx={{
                                    fontSize: "12px",
                                    color: "#6B7280",
                                }}
                            >
                                Use the upload button to import a text file.
                            </Box>
                        </Box>
                    )}
                </Box>

                {focusMode && (
                    <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
                        <Box
                            sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                height: "35%",
                                background:
                                    "linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0) 100%)",
                            }}
                        />
                        <Box
                            sx={{
                                position: "absolute",
                                bottom: 0,
                                left: 0,
                                right: 0,
                                height: "35%",
                                background:
                                    "linear-gradient(0deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0) 100%)",
                            }}
                        />
                        <Box
                            sx={{
                                position: "absolute",
                                top: "50%",
                                left: 16,
                                right: 16,
                                height: 56,
                                transform: "translateY(-50%)",
                                borderRadius: "10px",
                                border: "1px solid rgba(43, 107, 253, 0.12)",
                                backgroundColor: "rgba(255,255,255,0.6)",
                            }}
                        />
                    </Box>
                )}
            </Box>

            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 2,
                    py: 1,
                    backgroundColor: "#FAFAFA",
                    borderTop: "1px solid #E5E7EB",
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 160 }}>
                    <Tooltip title="Text size">
                        <TextFieldsOutlinedIcon sx={{ fontSize: 18, color: "#6B7280" }} />
                    </Tooltip>
                    <Slider
                        size="small"
                        value={fontSize}
                        min={MIN_FONT_SIZE}
                        max={MAX_FONT_SIZE}
                        onChange={(_, value) => setFontSize(value)}
                        valueLabelDisplay="auto"
                        sx={{ color: "#1F2937", width: 130 }}
                    />
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Tooltip title="Restart">
                        <IconButton size="small" onClick={handleRestart}>
                            <RestartAltOutlinedIcon sx={{ fontSize: 18, color: "#6B7280" }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={isPlaying ? "Pause" : "Play"}>
                        <IconButton size="small" onClick={handleTogglePlay}>
                            {isPlaying ? (
                                <PauseOutlinedIcon sx={{ fontSize: 18, color: "#1F2937" }} />
                            ) : (
                                <PlayArrowOutlinedIcon sx={{ fontSize: 18, color: "#1F2937" }} />
                            )}
                        </IconButton>
                    </Tooltip>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 160, justifyContent: "flex-end" }}>
                    <Tooltip title="Scroll speed">
                        <SpeedOutlinedIcon sx={{ fontSize: 18, color: "#6B7280" }} />
                    </Tooltip>
                    <Slider
                        size="small"
                        value={scrollSpeed}
                        min={MIN_SCROLL_SPEED}
                        max={MAX_SCROLL_SPEED}
                        onChange={(_, value) => setScrollSpeed(value)}
                        valueLabelDisplay="auto"
                        disabled={speedLocked}
                        sx={{ color: "#1F2937", width: 130 }}
                    />
                    <Tooltip title="Settings">
                        <IconButton size="small" onClick={(event) => setSettingsAnchorEl(event.currentTarget)}>
                            <SettingsOutlinedIcon sx={{ fontSize: 18, color: "#6B7280" }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            <Popover
                open={settingsOpen}
                anchorEl={settingsAnchorEl}
                onClose={() => setSettingsAnchorEl(null)}
                sx={{ zIndex: 1600 }}
                anchorOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                transformOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                }}
                PaperProps={{
                    sx: {
                        borderRadius: "12px",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                        minWidth: 240,
                        p: 2,
                    },
                }}
            >
                <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#1F2937", mb: 1.5 }}>
                    Teleprompter settings
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                    <Typography sx={{ fontSize: "12px", color: "#6B7280" }}>Scroll speed</Typography>
                    <FormControl size="small">
                        <Select
                            value={scrollMode}
                            onChange={(event) => setScrollMode(event.target.value)}
                            sx={{
                                minWidth: 120,
                                "& .MuiSelect-select": {
                                    py: 0.6,
                                    px: 1.1,
                                    fontSize: "12px",
                                },
                                "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#E5E7EB",
                                    borderRadius: "8px",
                                },
                            }}
                        >
                            <MenuItem value="variable">Variable</MenuItem>
                            <MenuItem value="constant">Constant</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
                <Divider sx={{ my: 1.5 }} />
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                    <Typography sx={{ fontSize: "12px", color: "#6B7280" }}>
                        Auto-scroll on record
                    </Typography>
                    <Switch
                        checked={autoScrollOnRecord}
                        onChange={(event) => setAutoScrollOnRecord(event.target.checked)}
                        size="small"
                        sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": { color: "#1F2937" },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                backgroundColor: "#1F2937",
                            },
                        }}
                    />
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Typography sx={{ fontSize: "12px", color: "#6B7280" }}>Focus mode</Typography>
                    <Switch
                        checked={focusMode}
                        onChange={(event) => setFocusMode(event.target.checked)}
                        size="small"
                        sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": { color: "#1F2937" },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                                backgroundColor: "#1F2937",
                            },
                        }}
                    />
                </Box>
            </Popover>

            <input
                ref={fileInputRef}
                type="file"
                accept=".txt,.md"
                style={{ display: "none" }}
                onChange={handleFileChange}
            />
        </Box>
    );
};

export default TeleprompterWindow;

